import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";

// Import all your templates
import PremiumModernTemplate from "../Package/PackagePdfTemplates/PremiumModernTemplate";
import SimpleEleganceTemplate from "./PackagePdfTemplates/SimpleEleganceTemplate";
import ModernCorporateTemplate from "./PackagePdfTemplates/ModernCorporateTemplate";
import LuxuryMagazineTemplate from "./PackagePdfTemplates/LuxuryMagazineTemplate";
import VibrantAdventureTemplate from "./PackagePdfTemplates/VibrantAdventureTemplate";
import ClassicEleganceTemplate from "./PackagePdfTemplates/ClassicEleganceTemplate";
import GeoExplorerTemplate from "./PackagePdfTemplates/GeoExplorerTemplate";
import ArtisanJournalTemplate from "./PackagePdfTemplates/ArtisanJournalTemplate";
import SimpleTemplate from "./PackagePdfTemplates/SimpleTemplate";

// --- STYLES FOR THE DOCUMENT PREVIEW PAGE ---
const styles = {
  // A neutral background for the page to make the documents stand out
  pageBackground: {
    backgroundColor: '#e0e0e0',
    padding: '2rem 0',
  },
  // Container for all the pages, centers them and adds spacing
  pagesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '2rem', // The space between pages
  },
  // The style for each individual A4 page
  a4Page: {
    width: '210mm', // Standard A4 width
    minHeight: '297mm', // Standard A4 height
    backgroundColor: 'white',
    boxShadow: '0 0 15px rgba(0,0,0,0.2)',
    overflow: 'hidden', // This clips the content to the page boundary
    position: 'relative',
  },
  // The content that gets rendered inside a page
  contentWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: 'auto',
  },
};

// --- NEW REUSABLE PAGINATION COMPONENT ---
// This component measures its child's height and renders it across multiple A4 pages.
const FullPagePaginatedView = ({ children }) => {
  const [totalPages, setTotalPages] = useState(1);
  const contentRef = useRef(null);

  // An A4 page is 297mm tall. We'll measure against this.
  const A4_HEIGHT_MM = 297;

  // Converts millimeters to pixels
  const mmToPx = (mm) => {
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.height = `${mm}mm`;
    document.body.appendChild(div);
    const px = div.offsetHeight;
    document.body.removeChild(div);
    return px;
  };

  useLayoutEffect(() => {
    // Set the body background color for the viewer effect
    document.body.style.backgroundColor = styles.pageBackground.backgroundColor;

    if (contentRef.current) {
      const pageHeightPx = mmToPx(A4_HEIGHT_MM);
      const observer = new ResizeObserver(entries => {
        const contentHeight = entries[0].target.scrollHeight;
        const pages = Math.ceil(contentHeight / pageHeightPx);
        setTotalPages(pages > 0 ? pages : 1);
      });
      observer.observe(contentRef.current);
      return () => {
        observer.disconnect();
        document.body.style.backgroundColor = ''; // Cleanup on unmount
      };
    }
    // Cleanup function
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, [children]);
  
  const pageHeightPx = mmToPx(A4_HEIGHT_MM);

  return (
    <>
      {/* 1. Render content invisibly at full A4 width to measure its natural height */}
      <div style={{ position: 'absolute', opacity: 0, zIndex: -10, width: '210mm' }}>
        <div ref={contentRef}>{children}</div>
      </div>

      {/* 2. Render the visible, paginated view */}
      <div style={styles.pagesContainer}>
        {Array.from({ length: totalPages }).map((_, index) => (
          <div key={index} style={styles.a4Page}>
            <div style={{
                ...styles.contentWrapper,
                // The magic: Slide the content up by one page height for each page
                transform: `translateY(-${index * pageHeightPx}px)`,
            }}>
              {children}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const TemplatePreviewPage = () => {
  const { packageId, templateId } = useParams();
  const { user } = useAuth();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);

  // The template mapping should be consistent with PackageTemplates.js
  const templates = [
    { id: "simple-elegance", name: "Simple Elegance", component: SimpleEleganceTemplate },
    { id: "classic-elegance", name: "Classic Elegance", component: ClassicEleganceTemplate },
    { id: "modern-corporate", name: "Modern Corporate", component: ModernCorporateTemplate },
    { id: "vibrant-adventure", name: "Vibrant Adventure", component: VibrantAdventureTemplate },
    { id: "geo-explorer", name: "Geo Explorer", component: GeoExplorerTemplate },
    { id: "premium-modern", name: "Premium Modern", component: PremiumModernTemplate },
    { id: "luxury-magazine", name: "Luxury Magazine", component: LuxuryMagazineTemplate },
    { id: "artisan-journal", name: "Artisan Journal", component: ArtisanJournalTemplate },
    { id: "simple-template", name: "Simple Template", component: SimpleTemplate },
  ];

  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        const res = await apiService.get(`${config.GETPACKAGEDETAILS}/${packageId}`);
        setPackageData(res);
      } catch (error) {
        console.error("Error fetching package details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (packageId) {
      fetchPackageData();
    }
  }, [packageId]);

  const renderTemplate = () => {
    if (!packageData || !templateId) return null;
    
    const template = templates.find((t) => t.id === templateId);
    if (!template) return <div>Template not found.</div>;
    
    const TemplateComponent = template.component;
    return <TemplateComponent packageData={packageData} user={user} />;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh', backgroundColor: styles.pageBackground.backgroundColor }}>
        <Spinner animation="border" /> <span className="ms-2">Loading Template Preview...</span>
      </div>
    );
  }

  // The main return now uses the PaginatedView component
  return (
    <FullPagePaginatedView>
      {renderTemplate()}
    </FullPagePaginatedView>
  );
};

export default TemplatePreviewPage;